/**
 * Typed epoch handling — makes "seconds vs millis" and "instant vs calendar day" compile-time
 * distinctions instead of runtime surprises.
 *
 * WHY THIS EXISTS
 * The OpenAPI client generates every date field as a bare `number`
 * (e.g. `WeightTrackRecord.date?: number`). That single type covers two incompatible things:
 *
 *   - `AdminWeightsController` emits `WeightTrackRecord.date` in epoch **SECONDS**
 *   - `WeightTrackService` emits the same field in epoch **MILLIS**
 *
 * They differ by 1000x, and because both are `number`, handing one to code expecting the other
 * typechecks cleanly and fails at runtime — a seconds value read as millis renders **January
 * 1970**. There is no server-side fix available today: shipped mobile builds parse the millis
 * form, so converging the units needs a coordinated client release.
 *
 * Branding closes the gap on this side. An `EpochSeconds` cannot be passed where `EpochMillis`
 * is expected (and vice versa) without an explicit, greppable conversion, so the mistake becomes
 * a build error.
 *
 * INSTANT vs CALENDAR DAY — the other half of the problem
 * An INSTANT is a moment; every client renders it in the viewer's own zone, which is correct and
 * intended. A CALENDAR DAY ("the 15th") has no instant and must never be re-localized: routing it
 * through a timezone is what makes a weigh-in dated the 15th display as the 14th for a viewer west
 * of UTC. Calendar days are therefore modelled here as `CalendarDay` strings (`YYYY-MM-DD`), NOT
 * as numbers, so the type system keeps them out of the instant helpers entirely.
 *
 * NOTE: this file is hand-maintained and lives OUTSIDE `src/api/generated/`, which is overwritten
 * by `npm run gen:api:live`. Do not move it in there.
 */

/** Epoch SECONDS. The dominant convention across this API. */
export type EpochSeconds = number & {readonly __epochUnit: 'seconds'};

/** Epoch MILLISECONDS. JS-native, and what a handful of mobile endpoints emit. */
export type EpochMillis = number & {readonly __epochUnit: 'millis'};

/** A zone-less calendar day, `YYYY-MM-DD`. Deliberately a string, never a number. */
export type CalendarDay = string & {readonly __dateKind: 'calendarDay'};

const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Brand a number you KNOW is epoch seconds (e.g. a field documented as such). */
export const epochSeconds = (n: number): EpochSeconds => n as EpochSeconds;

/** Brand a number you KNOW is epoch millis. */
export const epochMillis = (n: number): EpochMillis => n as EpochMillis;

/** Brand a `YYYY-MM-DD` string, rejecting anything else rather than failing later. */
export const calendarDay = (s: string): CalendarDay => {
  if (!CALENDAR_DAY_PATTERN.test(s)) {
    throw new Error(`Not a YYYY-MM-DD calendar day: "${s}"`);
  }
  return s as CalendarDay;
};

// ---------------------------------------------------------------------------------------------
// Instants — safe to localize; the viewer's zone is the point.
// ---------------------------------------------------------------------------------------------

export const secondsToDate = (secs: EpochSeconds): Date => new Date(secs * 1000);

export const millisToDate = (ms: EpochMillis): Date => new Date(ms);

export const dateToEpochSeconds = (d: Date): EpochSeconds =>
  Math.floor(d.getTime() / 1000) as EpochSeconds;

export const dateToEpochMillis = (d: Date): EpochMillis => d.getTime() as EpochMillis;

/** Explicit, greppable unit conversions — the only sanctioned way to cross the boundary. */
export const secondsToMillis = (secs: EpochSeconds): EpochMillis =>
  (secs * 1000) as EpochMillis;

export const millisToSeconds = (ms: EpochMillis): EpochSeconds =>
  Math.floor(ms / 1000) as EpochSeconds;

// ---------------------------------------------------------------------------------------------
// Calendar days — never localized.
// ---------------------------------------------------------------------------------------------

/**
 * The LOCAL calendar day of a Date.
 *
 * Uses local getters, never `toISOString()`: the latter converts to UTC first, so for a user east
 * of UTC (Israel is UTC+2/+3) a time between local midnight and 02:00/03:00 reports YESTERDAY.
 */
export const localCalendarDay = (d: Date): CalendarDay => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}` as CalendarDay;
};

/** Today, as the USER's device reckons it. */
export const todayLocal = (): CalendarDay => localCalendarDay(new Date());

/**
 * Decode a calendar day the server sent as epoch seconds at UTC midnight.
 *
 * Read at UTC on purpose: localizing it is what shifts the day. Use this — not
 * `secondsToDate` + a local formatter — for anything the server models as a `LocalDate`.
 */
export const utcMidnightSecondsToCalendarDay = (secs: EpochSeconds): CalendarDay => {
  const d = new Date(secs * 1000);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}` as CalendarDay;
};

/** Encode a calendar day as the epoch seconds at UTC midnight the server expects. */
export const calendarDayToUtcMidnightSeconds = (day: string): EpochSeconds => {
  const validated = calendarDay(day);
  const [y, m, d] = validated.split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 1000) as EpochSeconds;
};

/**
 * Normalize the shapes a `LocalDate` can arrive in. Jackson serializes it as a `[y, m, d]` array
 * when `WRITE_DATES_AS_TIMESTAMPS` is on (the current server default), while some endpoints send
 * a plain ISO string — so both have to be handled at the boundary.
 */
export const parseCalendarDay = (
  value: string | number[] | null | undefined,
): CalendarDay | null => {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const [y, m, d] = value;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` as CalendarDay;
  }
  const asString = String(value).slice(0, 10);
  return CALENDAR_DAY_PATTERN.test(asString) ? (asString as CalendarDay) : null;
};
